import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DmsPage,
  intl,
  HTTP_STATUS,
  DmsTable,
  fetchData,
  DmsButton,
  DmsButtonGroup,
  DmsAltBox,
  DmsCalendar,
  DmsAlertWarning,
  DmsAlertInfo,
  DmsSwitch,
  DmsTextField,
  DmsTabber,
  DmsModal,
} from "dmsbasereact";
import Swal from "sweetalert2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MedicationIcon from "@mui/icons-material/Medication";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { Box, Chip, CircularProgress, Typography, Tooltip, Grid } from "@mui/material";
import dayjs from 'dayjs';
import { PatientStatusEnum, PatientStatus } from '../../constants/PatientStatusEnum';
import { PatientStatusReason } from '../../constants/PatientStatusReason';
import { PatientDetailModal } from "../../components/PatientDetailModal";
import { PrescriptionModal } from "../../components/PrescriptionModal";
import { ModalFormCreatePatient } from "../../components/ModalFormCreatePatient/ModalFormCreatePatient";
import { getGenderEnum } from "../../constants/GenderEnum";
import { DmsStatusChangeModal } from "../../components/DmsStatusChangeModal";
import { useInstitutionLinkDeclaration } from "../../hooks/useInstitutionLinkDeclaration";
import { PRESCRIPTION_STATUS } from "../../constants/PrescriptionStatus";
import { formatCpf } from "../../utils/formatters";
import { DmsTextFieldCpf } from "../../components/DmsTextFieldCpf/DmsTextFieldCpf";
import { isExpired } from "../../utils/expiration";
import { APP_ROUTES } from "../../utils/NamedRoutes";

export const MyPatientsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is a doctor by checking for doctor-specific permissions
  // EUROFARMA_GESTAO_LENA users have MY_PATIENTS_PAGE but are NOT doctors
  const [isDoctor, setIsDoctor] = useState(null); // null = checking, true/false = result
  const [isViewerMode, setIsViewerMode] = useState(false);

  // Check if user is a doctor on component mount
  useEffect(() => {
    const checkIfDoctor = async () => {
      try {
        const response = await fetchData("/prescription/check_doctor", "GET", true);
        const isDoctorResult = response?.data?.is_doctor === true;
        setIsDoctor(isDoctorResult);
        setIsViewerMode(!isDoctorResult); // Viewer mode if NOT a doctor
      } catch (err) {
        // If check fails, assume viewer mode for safety
        setIsDoctor(false);
        setIsViewerMode(true);
      }
    };
    checkIfDoctor();
  }, []);

  // Check if doctor has accepted the institution link declaration
  // Only relevant for actual doctors, not viewers
  const { loading: declarationLoading, isDeclarationRequired, blockIfRequired } = useInstitutionLinkDeclaration('doctor');

  // Block page access if declaration is required (only for doctors, not viewers)
  useEffect(() => {
    if (!declarationLoading && isDoctor === true) {
      blockIfRequired();
    }
  }, [declarationLoading, blockIfRequired, isDoctor]);
  const table = useRef(null);
  const inactiveTable = useRef(null);

  // Tab state - check for navigation state to open specific tab
  const [activeTab, setActiveTab] = useState(location.state?.tab || 0);

  // Create patient modal state
  const [createPatientModalOpen, setCreatePatientModalOpen] = useState(false);

  // Inactive patients alert state
  const [inactivePatientPks, setInactivePatientPks] = useState([]);

  // Deactivation modal state
  const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
  const [selectedPatientForStatus, setSelectedPatientForStatus] = useState(null);

  // Activation modal state (for DEATH reactivation)
  const [isActivationModalOpen, setActivationModalOpen] = useState(false);
  const [selectedPatientForActivation, setSelectedPatientForActivation] = useState(null);

  // Patient detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPatientDetail, setSelectedPatientDetail] = useState(null);
  const [loadingPatientDetail, setLoadingPatientDetail] = useState(false);

  // Prescription modal state
  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
  const [prescriptionPatient, setPrescriptionPatient] = useState(null);

  // Active prescription warning modal state
  const [showActivePrescriptionWarning, setShowActivePrescriptionWarning] = useState(false);
  const [activePrescriptionForWarning, setActivePrescriptionForWarning] = useState(null);
  const [isOwnPrescription, ] = useState(false);
  const [currentDoctorPk, setCurrentDoctorPk] = useState(null);

  // Recent prescription blocking modal state (19-day rule)
  const [showRecentPrescriptionModal, setShowRecentPrescriptionModal] = useState(false);
  const [recentPrescriptionInfo, setRecentPrescriptionInfo] = useState(null);

  // Patient search state
  const [searchCpf, setSearchCpf] = useState('');
  const [searchDob, setSearchDob] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isPatientInList, setIsPatientInList] = useState(false);
  const [isAddingToList, setIsAddingToList] = useState(false);

  // Search patient by CPF, DOB and Name
  const handleSearchPatient = useCallback(async () => {
    if (!searchCpf || !searchDob || !searchName.trim()) {
      Swal.fire({
        icon: "warning",
        title: intl.get("MissingInformation").d("Missing Information"),
        text: intl.get("enter_cpf_dob_and_name").d("Please enter CPF, Date of Birth and Full Name.")
      });
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResult(null);
    setIsPatientInList(false);

    try {
      const cleanedCpf = typeof searchCpf === 'string' ? searchCpf.replace(/\D/g, '') : '';
      const formattedDob = dayjs(searchDob).format('YYYY-MM-DD');
      const trimmedName = searchName.trim();
      // Handle case where searchCpf is undefined or not a string
      if (typeof searchCpf !== 'string' || !cleanedCpf) {
        setSearchError('not_found');
        setSearchLoading(false);
        return;
      }
      const response = await fetchData(
        `/patient/find_by_cpf_dob_name?cpf=${cleanedCpf}&dob=${formattedDob}&name=${encodeURIComponent(trimmedName)}`,
        'GET',
        true
      );

      if (response.status === HTTP_STATUS.OK_200) {
        const patient = response.data.data || response.data;
        setSearchResult(patient);

        // Check if patient is already in doctor's list
        const listResponse = await fetchData('/doctor/patients_with_prescriptions', 'GET', true);
        if (listResponse.status === HTTP_STATUS.OK_200) {
          const patients = listResponse.data?.data?.rows || [];
          const isInList = patients.some(p => p.pk === patient.pk);
          setIsPatientInList(isInList);
        }
      } else if (response.status === HTTP_STATUS.NOT_FOUND_404) {
        setSearchError('not_found');
      } else {
        setSearchError('error');
      }
    } catch (error) {
      console.error("Error searching patient:", error);
      if (error.response?.status === 404) {
        setSearchError('not_found');
      } else {
        setSearchError('error');
      }
    }

    setSearchLoading(false);
  }, [searchCpf, searchDob, searchName]);

  // Add patient to doctor's list
  const handleAddToList = useCallback(async () => {
    if (!searchResult) return;

    setIsAddingToList(true);

    try {
      const response = await fetchData('/doctor/add_patient', 'POST', true, {
        patient_pk: searchResult.pk
      });

      if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
        setIsPatientInList(true);
        table.current?.updateTable();
        Swal.fire({
          icon: "success",
          title: intl.get("Success").d("Success"),
          text: intl.get("patient_added_to_list").d("Patient added to your list successfully!")
        });
      } else if (response.status === HTTP_STATUS.CONFLICT_409) {
        setIsPatientInList(true);
        Swal.fire({
          icon: "info",
          title: intl.get("Info").d("Info"),
          text: intl.get("patient_already_in_list").d("Patient is already in your list.")
        });
      } else {
        throw new Error(response.data?.detail || "Failed to add patient");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: error.message || intl.get("add_patient_error").d("Error adding patient to your list. Please try again.")
      });
    }

    setIsAddingToList(false);
  }, [searchResult]);

  // Clear search results
  const handleClearSearch = useCallback(() => {
    setSearchCpf('');
    setSearchDob(null);
    setSearchName('');
    setSearchResult(null);
    setSearchError(null);
    setIsPatientInList(false);
  }, []);

  // Open prescription modal (with 19-day rule check)
  const handlePrescribe = useCallback(async (patient) => {
    // Fetch all prescriptions for the patient to check blocking rule
    try {
      const prescriptionsResponse = await fetchData(`/prescription/get_from_patient/${patient.pk}?show_all=true`, 'GET', true);

      if (prescriptionsResponse.status === 200) {
        const prescriptions = prescriptionsResponse.data?.data || [];

        // Filter out CANCELED prescriptions
        const validPrescriptions = prescriptions.filter(p => p.status !== PRESCRIPTION_STATUS.CANCELED);

        if (validPrescriptions.length > 0) {
          // Sort by prescription_date to get the most recent
          const mostRecent = validPrescriptions.sort((a, b) =>
            new Date(b.prescription_date) - new Date(a.prescription_date)
          )[0];

          const prescriptionExpired = isExpired(mostRecent.expiration_date);
          const isActive = mostRecent.status === PRESCRIPTION_STATUS.ACTIVE;
          const isDispensed = mostRecent.status === PRESCRIPTION_STATUS.DISPENSED;

          // Case 1: Active prescription that is not expired
          if (isActive && !prescriptionExpired) {
            const expirationDate = new Date(mostRecent.expiration_date);
            const today = new Date();
            const daysRemaining = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));

            setRecentPrescriptionInfo({
              prescription: mostRecent,
              daysRemaining: daysRemaining,
              nextPrescriptionDate: expirationDate,
              isOwnPrescription: mostRecent.doctor_pk === currentDoctorPk,
              blockReason: 'active' // To differentiate in modal
            });
            setShowRecentPrescriptionModal(true);
            return;
          }

          // Case 2: Dispensed prescription within 19 days
          if (isDispensed) {
            const prescriptionDate = new Date(mostRecent.prescription_date);
            const minNewPrescriptionDate = new Date(prescriptionDate);
            minNewPrescriptionDate.setDate(minNewPrescriptionDate.getDate() + 19);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            minNewPrescriptionDate.setHours(0, 0, 0, 0);

            if (today <= minNewPrescriptionDate) {
              const daysRemaining = Math.ceil((minNewPrescriptionDate - today) / (1000 * 60 * 60 * 24));
              setRecentPrescriptionInfo({
                prescription: mostRecent,
                daysRemaining: daysRemaining,
                nextPrescriptionDate: minNewPrescriptionDate,
                isOwnPrescription: mostRecent.doctor_pk === currentDoctorPk,
                blockReason: 'dispensed' // To differentiate in modal
              });
              setShowRecentPrescriptionModal(true);
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking prescriptions:', error);
      // Continue to open modal even if check fails
    }

    // No blocking prescription, open modal as normal
    setPrescriptionPatient(patient);
    setPrescriptionModalOpen(true);
  }, [currentDoctorPk]);

  // Close prescription modal
  const handleClosePrescriptionModal = useCallback(() => {
    setPrescriptionModalOpen(false);
    setPrescriptionPatient(null);
  }, []);

  // Handle prescription success
  const handlePrescriptionSuccess = useCallback(() => {
    table.current?.updateTable();
    handleClearSearch();
  }, [handleClearSearch]);

  // Fetch patient details
  const handleViewDetails = useCallback(async (patient) => {
    setLoadingPatientDetail(true);
    setDetailModalOpen(true);

    try {
      const response = await fetchData(`/patient/${patient.pk}/details`, 'GET', true);
      if (response.status === HTTP_STATUS.OK_200) {
        setSelectedPatientDetail(response.data);
      } else {
        setDetailModalOpen(false);
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: intl.get("failed_load_patient_details").d("Failed to load patient details")
        });
      }
    } catch (error) {
      setDetailModalOpen(false);
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: error.message
      });
    }

    setLoadingPatientDetail(false);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedPatientDetail(null);
  }, []);

  // Save patient details
  const handleSavePatient = useCallback(async (patientPk, payload) => {
    try {
      const response = await fetchData(`/patient/${patientPk}`, 'PUT', true, payload);
      if (response.status === HTTP_STATUS.OK_200) {
        Swal.fire({
          icon: "success",
          title: intl.get("Success").d("Success"),
          text: intl.get("patient_updated_success").d("Patient updated successfully!")
        });
        const detailsResponse = await fetchData(`/patient/${patientPk}/details`, 'GET', true);
        if (detailsResponse.status === HTTP_STATUS.OK_200) {
          return detailsResponse.data;
        }
        return response.data;
      } else {
        const errorDetail = response.data?.detail;
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: typeof errorDetail === 'string' ? errorDetail : errorDetail?.message || intl.get("patient_update_failed").d("Failed to update patient")
        });
        return null;
      }
    } catch (error) {
      const backendMessage = error?.response?.data?.detail || error?.response?.data;
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: typeof backendMessage === "string" ? backendMessage : intl.get("patient_update_failed").d("Failed to update patient")
      });
      return null;
    }
  }, []);

  // Refresh table after patient update
  const handlePatientUpdated = useCallback(() => {
    table.current?.updateTable();
  }, []);

  // Fetch current doctor pk on mount
  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const response = await fetchData('/prescription/check_doctor', 'GET', true);
        if (response.status === HTTP_STATUS.OK_200 && response.data) {
          setCurrentDoctorPk(response.data.doctor_pk);
        }
      } catch (error) {
        console.error("Error fetching doctor info:", error);
      }
    };

    fetchDoctorInfo();
  }, []);

  // Fetch inactive patients on mount
  useEffect(() => {
    const fetchInactivePatients = async () => {
      try {
        const response = await fetchData('/doctor/inactive_patients', 'GET', true);
        if (response.status === HTTP_STATUS.OK_200 && response.data.data) {
          setInactivePatientPks(response.data.data.map(p => p.pk));
        }
      } catch (error) {
        console.error("Error fetching inactive patients:", error);
      }
    };

    fetchInactivePatients();
  }, []);

  /**
   * Check if patient was inactivated due to DEATH
   * @param {Object} patient - Patient object from table
   * @returns {boolean} True if patient was inactivated due to DEATH
   */
  const checkIfDeathReason = (patient) => {
    // Check if deactivation_reason is DEATH (from /doctor/deactivated_patients endpoint)
    if (patient.deactivation_reason === "DEATH") {
      return true;
    }

    // Fallback: Check patient_status_reason object if available
    if (patient.patient_status_reason?.name === "DEATH") {
      return true;
    }

    return false;
  };

  // Handle status switch toggle
  const handleStatusToggle = useCallback((patient, newIsActive) => {
    if (newIsActive) {
      // Check if patient was inactivated due to DEATH
      const wasDeathReason = checkIfDeathReason(patient);

      if (wasDeathReason) {
        // DEATH reactivation - show justification modal
        setSelectedPatientForActivation(patient);
        setActivationModalOpen(true);
      } else {
        // Normal activation - call API directly (current behavior)
        handleActivatePatient(patient);
      }
    } else {
      // Deactivating - open modal to select reason (unchanged)
      setSelectedPatientForStatus(patient);
      setDeactivationModalOpen(true);
    }
  }, []);

  // Activate patient (no justification needed)
  const handleActivatePatient = async (patient) => {
    try {
      const response = await fetchData(`/patient/${patient.pk}/toggle_status`, 'PUT', true, {
        is_active: true
      });

      if (response.status === HTTP_STATUS.OK_200) {
        Swal.fire({
          icon: "success",
          title: intl.get("Success").d("Success"),
          text: intl.get("patient_activated_success").d("Patient activated successfully!")
        });
        table.current?.updateTable();
        inactiveTable.current?.updateTable();
      } else {
        throw new Error(response?.data?.detail || intl.get("patient_activation_failed").d("Failed to activate patient"));
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: error.message
      });
      // Reload both tables to revert switch visual state on error
      table.current?.updateTable();
      inactiveTable.current?.updateTable();
    }
  };

  // Close deactivation modal
  const handleCloseDeactivationModal = useCallback(() => {
    setDeactivationModalOpen(false);
    setSelectedPatientForStatus(null);
    // Reload table to revert switch visual state if modal was cancelled
    table.current?.updateTable();
  }, []);

  // Handle deactivation success
  const handleDeactivationSuccess = useCallback(() => {
    Swal.fire({
      icon: "success",
      title: intl.get("Success").d("Success"),
      text: intl.get("patient_deactivated_success").d("Patient deactivated successfully!")
    });
    table.current?.updateTable();
    inactiveTable.current?.updateTable();
  }, []);

  // Close activation modal
  const handleCloseActivationModal = useCallback(() => {
    setActivationModalOpen(false);
    setSelectedPatientForActivation(null);
    // Reload table to revert switch visual state if modal was cancelled
    table.current?.updateTable();
    inactiveTable.current?.updateTable();
  }, []);

  // Handle activation success
  const handleActivationSuccess = useCallback(() => {
    Swal.fire({
      icon: "success",
      title: intl.get("Success").d("Success"),
      text: intl.get("patient_activated_success").d("Patient activated successfully!")
    });
    table.current?.updateTable();
    inactiveTable.current?.updateTable();
  }, []);

  const getPatientStatusColor = (status) => {
    switch (status) {
      case PatientStatusEnum.ACTIVE:
        return 'success';
      case PatientStatusEnum.INACTIVE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPatientStatusLabel = (status) => {
    return PatientStatus.getText(status);
  };

  const header = [
    {
      label: intl.get("patient_identifier").d("Patient ID"),
      column_name: "patient_identifier",
      filter: true
    },
    {
      label: intl.get("patient_name").d("Patient Name"),
      column_name: "name",
      filter: true,
      element: (row) => {
        const isInactive = inactivePatientPks.includes(row.pk);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isInactive && (
              <Tooltip
                title={intl.get("patient_inactive_warning").d("Patient without prescription for over 6 months")}
                arrow
              >
                <WarningIcon color="warning" sx={{ mr: 1, cursor: 'help' }} />
              </Tooltip>
            )}
            {row.name}
          </Box>
        );
      }
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "cpf",
      filter: true,
      function: (cpf) => formatCpf(cpf)
    },
    {
      label: intl.get("last_prescription").d("Last Prescription"),
      column_name: "active_prescription",
      sortable: false,
      function: (activePrescription) => {
        if (!activePrescription) return '-';
        return activePrescription.prescription_identifier || '-';
      }
    },
    {
      label: intl.get("ExpirationDate").d("Expiration Date"),
      column_name: "active_prescription",
      sortable: false,
      function: (activePrescription) => {
        if (!activePrescription || !activePrescription.expiration_date) return '-';
        return dayjs(activePrescription.expiration_date).format('DD/MM/YYYY');
      }
    },
    {
      label: intl.get("status").d("Status"),
      column_name: "status",
      sortable: true,
      element: (row) => {
        const isActive = row.status === PatientStatusEnum.ACTIVE;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status toggle - only for doctors, not viewers */}
            {!isViewerMode && (
              <Tooltip
                title={intl.get(isActive ? "deactivate_patient_tooltip" : "activate_patient_tooltip")
                  .d(isActive ? "Click to deactivate patient" : "Click to activate patient")}
                arrow
              >
                <Box>
                  <DmsSwitch
                    name={`patient_status_${row.pk}`}
                    checked={isActive}
                    onChange={(name, value) => handleStatusToggle(row, value === "true")}
                  />
                </Box>
              </Tooltip>
            )}
            <Chip
              label={getPatientStatusLabel(row.status)}
              color={getPatientStatusColor(row.status)}
              size="small"
            />
          </Box>
        );
      }
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      sortable: false,
      element: (row) => {
        return (
          <DmsButtonGroup>
            <DmsButton
              icon={<VisibilityIcon />}
              onClick={() => handleViewDetails(row)}
              title={intl.get("view_details").d("View Details")}
            />

            {/* Prescribe button - only for doctors, not viewers */}
            {!isViewerMode && (
              <DmsButton
                icon={<MedicationIcon />}
                onClick={() => handlePrescribe(row)}
                title={intl.get("prescribe").d("Prescribe")}
              />
            )}
          </DmsButtonGroup>
        );
      },
    },
  ];

  // Header for inactive patients tab
  const inactiveHeader = [
    {
      label: intl.get("patient_identifier").d("Patient ID"),
      column_name: "patient_identifier",
      filter: true
    },
    {
      label: intl.get("patient_name").d("Patient Name"),
      column_name: "name",
      filter: true
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "cpf",
      filter: true,
      function: (cpf) => formatCpf(cpf)
    },
    {
      label: intl.get("status").d("Status"),
      column_name: "status",
      sortable: true,
      element: (row) => {
        const isActive = row.status === PatientStatusEnum.ACTIVE;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status toggle - only for doctors, not viewers */}
            {!isViewerMode && (
              <Tooltip
                title={intl.get(isActive ? "deactivate_patient_tooltip" : "activate_patient_tooltip")
                  .d(isActive ? "Click to deactivate patient" : "Click to activate patient")}
                arrow
              >
                <Box>
                  <DmsSwitch
                    name={`patient_status_${row.pk}`}
                    checked={isActive}
                    onChange={(name, value) => handleStatusToggle(row, value === "true")}
                  />
                </Box>
              </Tooltip>
            )}
            <Chip
              label={getPatientStatusLabel(row.status)}
              color={getPatientStatusColor(row.status)}
              size="small"
            />
          </Box>
        );
      }
    },
    {
      label: intl.get("deactivation_reason").d("Reason"),
      column_name: "deactivation_reason",
      sortable: false,
      function: (reason) => reason ? PatientStatusReason.getText(reason) : '-'
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      sortable: false,
      element: (row) => {
        return (
          <DmsButtonGroup>
            <DmsButton
              icon={<VisibilityIcon />}
              onClick={() => handleViewDetails(row)}
              title={intl.get("view_details").d("View Details")}
            />
          </DmsButtonGroup>
        );
      },
    },
  ];

  // Show loading while checking if user is a doctor
  if (isDoctor === null) {
    return (
      <DmsPage name={intl.get("my_patients").d("My Patients")}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </DmsPage>
    );
  }

  // Show loading while checking declaration status (only for doctors)
  if (isDoctor && declarationLoading) {
    return (
      <DmsPage name={intl.get("my_patients").d("My Patients")}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </DmsPage>
    );
  }

  // Don't render page content if declaration is required (modal will show and redirect) - only for doctors
  if (isDoctor && isDeclarationRequired) {
    return (
      <DmsPage name={intl.get("my_patients").d("My Patients")}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </DmsPage>
    );
  }

  // Page title changes for viewer mode
  const pageTitle = isViewerMode
    ? intl.get("all_patients").d("All Patients")
    : intl.get("my_patients").d("My Patients");

  return (
    <DmsPage name={pageTitle}>
      <DmsAltBox.Card>
        <DmsAltBox.Header>
          <Typography variant="h6">
            {pageTitle}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          {/* Add Patient Section - only for doctors, not viewers */}
          {!isViewerMode && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              <SearchIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
              {intl.get("find_patient_instruction").d("Use the fields below to locate an existing patient. If the patient does not exist, the system will enable the option to register a new patient.")}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <DmsTextField
                  name="name"
                  label={intl.get("patient_name").d("Patient Name")}
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  fullWidth
                  placeholder={intl.get("enter_full_name").d("Enter full name")}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <DmsTextFieldCpf
                  name="cpf"
                  label={intl.get("CPF").d("CPF")}
                  value={searchCpf}
                  onChange={(e) => setSearchCpf(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <DmsCalendar.DmsFlexiblePicker
                  pickerType="date"
                  label={intl.get("BirthDate").d("Birth Date")}
                  value={searchDob}
                  onChange={(newValue) => setSearchDob(newValue)}
                  format="DD/MM/YYYY"
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} sm={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, pb: '4px' }}>
                <DmsButton
                  icon={<SearchIcon />}
                  text={intl.get("search").d("Search")}
                  onClick={handleSearchPatient}
                  disabled={searchLoading}
                  fullWidth
                />
                {(searchResult || searchError) && (
                  <DmsButton
                    text={intl.get("Clear").d("Clear")}
                    onClick={handleClearSearch}
                    variant="secondary"
                    fullWidth
                  />
                )}
              </Grid>
            </Grid>

            {/* Search Results */}
            {searchLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {searchError === 'not_found' && (
              <Box sx={{ mt: 2 }}>
                <DmsAlertWarning
                  message={intl.get("patient_not_found_search").d("Patient not found. Check the CPF and date of birth or register a new patient.")}
                />
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <DmsButton
                    icon={<PersonAddIcon />}
                    text={intl.get("register_new_patient").d("Register New Patient")}
                    onClick={() => setCreatePatientModalOpen(true)}
                    variant="secondary"
                  />
                </Box>
              </Box>
            )}

            {searchError === 'error' && (
              <Box sx={{ mt: 2 }}>
                <DmsAlertWarning
                  message={intl.get("search_error").d("Error searching patient. Please try again.")}
                />
              </Box>
            )}

            {searchResult && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                      <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 18 }} />
                      {intl.get("patient_found").d("Patient Found")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {searchResult.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("CPF").d("CPF")}</Typography>
                        <Typography variant="body2">{formatCpf(searchResult.cpf)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("BirthDate").d("Birth Date")}</Typography>
                        <Typography variant="body2">{dayjs(searchResult.birth_date).format('DD/MM/YYYY')}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("gender").d("Gender")}</Typography>
                        <Typography variant="body2">
                          {searchResult.gender === getGenderEnum().M.value ? intl.get("male").d("Male") : intl.get("female").d("Female")}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("status").d("Status")}</Typography>
                        <Typography variant="body2">
                          <Chip
                            label={searchResult.status === 1 ? intl.get("Active").d("Active") : intl.get("Inactive").d("Inactive")}
                            color={searchResult.status === 1 ? "success" : "error"}
                            size="small"
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {!isPatientInList ? (
                      <DmsButton
                        icon={<PersonAddIcon />}
                        text={intl.get("add_and_prescribe").d("Add and Prescribe")}
                        onClick={async () => {
                          await handleAddToList();
                          handlePrescribe(searchResult);
                        }}
                        disabled={isAddingToList}
                      />
                    ) : (
                      <>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={intl.get("in_your_list").d("In your list")}
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                        <DmsButton
                          icon={<MedicationIcon />}
                          text={intl.get("prescribe").d("Prescribe")}
                          onClick={() => handlePrescribe(searchResult)}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          )}

          {/* Tabs for Active/Inactive Patients */}
          <DmsTabber
            selected={activeTab}
            onChange={(key) => setActiveTab(key)}
            tabs={[
              {
                title: intl.get("active_patients").d("Active Patients"),
                component: (
                  <Box>
                    {/* Legend */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <Tooltip title={intl.get("patient_inactive_warning").d("Patient without prescription for over 6 months")} arrow>
                          <WarningIcon color="warning" sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', cursor: 'help' }} />
                        </Tooltip>
                        {intl.get("legend_inactive_patient").d("Patient without prescription for over 6 months")}
                      </Typography>
                    </Box>

                    {/* Active Patients Table */}
                    <DmsTable
                      header={header}
                      managed={true}
                      needs_authentication={true}
                      ref={table}
                      url={'/doctor/patients_with_prescriptions'}
                      export_table={false}
                    />
                  </Box>
                )
              },
              {
                title: intl.get("inactive_patients_tab").d("Inactive Patients"),
                component: (
                  <Box>
                    {/* Info about inactive patients */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <PersonOffIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        {intl.get("inactive_patients_description").d("These are patients with inactive status. You can reactivate them at any time by changing their status.")}
                      </Typography>
                    </Box>

                    {/* Inactive Patients Table */}
                    <DmsTable
                      header={inactiveHeader}
                      managed={true}
                      needs_authentication={true}
                      ref={inactiveTable}
                      url={'/doctor/deactivated_patients'}
                      export_table={false}
                    />
                  </Box>
                )
              }
            ]}
          />
        </DmsAltBox.Body>
      </DmsAltBox.Card>

      {/* Deactivate Patient Modal with Justification */}
      {selectedPatientForStatus && (
        <DmsStatusChangeModal
          open={isDeactivationModalOpen}
          onClose={handleCloseDeactivationModal}
          entityPk={selectedPatientForStatus.pk}
          entityName={`${selectedPatientForStatus.name} (${selectedPatientForStatus.patient_identifier})`}
          currentStatus={PatientStatusEnum.ACTIVE}
          newStatus={PatientStatusEnum.INACTIVE}
          apiEndpoint="/patient"
          apiPathSuffix="/toggle_status"
          apiMethod="PUT"
          buildPayload={(reasonPk, customReason) => ({
            is_active: false,
            patient_status_reason_pk: reasonPk ? Number(reasonPk) : null,
            status_reason: customReason || null,
          })}
          onSuccess={handleDeactivationSuccess}
          title={intl.get("deactivate_patient").d("Deactivate Patient")}
          statusLabels={{
            [PatientStatusEnum.ACTIVE]: intl.get("Active").d("Active"),
            [PatientStatusEnum.INACTIVE]: intl.get("Inactive").d("Inactive"),
          }}
          reasonType="patient_status_reason"
          reasonLabel={intl.get("deactivation_reason").d("Reason")}
          reasonRequired
        />
      )}

      {/* Activation Justification Modal (for DEATH reactivation) */}
      {selectedPatientForActivation && (
        <DmsStatusChangeModal
          open={isActivationModalOpen}
          onClose={handleCloseActivationModal}
          entityPk={selectedPatientForActivation.pk}
          entityName={`${selectedPatientForActivation.name} (${selectedPatientForActivation.patient_identifier})`}
          currentStatus={PatientStatusEnum.INACTIVE}
          newStatus={PatientStatusEnum.ACTIVE}
          apiEndpoint="/patient"
          apiPathSuffix="/toggle_status"
          apiMethod="PUT"
          buildPayload={(reason) => ({
            is_active: true,
            status_reason: reason,
          })}
          onSuccess={handleActivationSuccess}
          title={intl.get("ReactivatePatientDeath").d("Reactivate Patient (Death Record)")}
          statusLabels={{
            [PatientStatusEnum.ACTIVE]: intl.get("Active").d("Active"),
            [PatientStatusEnum.INACTIVE]: intl.get("Inactive").d("Inactive"),
          }}
          reasonType={null}
          reasonLabel={intl.get("ReactivationJustification").d("Justification for Reactivation")}
          reasonRequired={true}
          reasonRows={4}
          confirmBeforeSave={true}
          confirmMessage={intl.get("ConfirmReactivateDeath").d("This patient was marked as DECEASED. Are you sure you want to reactivate? Please provide a justification.")}
        />
      )}

      {/* Patient Detail Modal */}
      <PatientDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        patient={selectedPatientDetail}
        loading={loadingPatientDetail}
        onSave={handleSavePatient}
        onPatientUpdated={handlePatientUpdated}
      />

      {/* Prescription Modal */}
      <PrescriptionModal
        open={prescriptionModalOpen}
        onClose={handleClosePrescriptionModal}
        patient={prescriptionPatient}
        onSuccess={handlePrescriptionSuccess}
      />

      {/* Create Patient Modal */}
      <ModalFormCreatePatient
        isVisible={createPatientModalOpen}
        onClose={() => {
          setCreatePatientModalOpen(false);
          table.current?.updateTable();
        }}
        table={table}
        initialCpf={searchCpf}
        initialDob={searchDob}
        initialName={searchName}
      />

      {/* Active Prescription Warning Modal */}
      <DmsModal
        isVisible={showActivePrescriptionWarning}
        onClose={() => {
          setShowActivePrescriptionWarning(false);
          setActivePrescriptionForWarning(null);
        }}
      >
        <DmsModal.Header
          title={isOwnPrescription
            ? intl.get("your_active_prescription_title").d("Your Active Prescription")
            : intl.get("active_prescription_error_title").d("Active Prescription Found")
          }
          onClose={() => {
            setShowActivePrescriptionWarning(false);
            setActivePrescriptionForWarning(null);
          }}
        />
        <DmsModal.Body>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              {isOwnPrescription
                ? intl.get("your_active_prescription_message").d("You already have an active prescription for this patient that is still valid. To create a new prescription, cancel the current prescription first.")
                : intl.get("other_doctor_active_prescription_message").d("This patient has an active prescription created by another doctor that is still valid. It is not possible to create a new prescription.")
              }
            </Typography>

            {activePrescriptionForWarning && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {intl.get("prescription_details").d("Prescription Details:")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("prescription_identifier").d("Prescription ID")}:</strong>{' '}
                      {activePrescriptionForWarning.prescription_identifier || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("prescription_date").d("Prescription Date")}:</strong>{' '}
                      {activePrescriptionForWarning.prescription_date ? dayjs(activePrescriptionForWarning.prescription_date).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("ExpirationDate").d("Expiration Date")}:</strong>{' '}
                      {activePrescriptionForWarning.expiration_date ? dayjs(activePrescriptionForWarning.expiration_date).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("status").d("Status")}:</strong>{' '}
                      {activePrescriptionForWarning.status === PRESCRIPTION_STATUS.ACTIVE
                        ? intl.get("Active").d("Active")
                        : intl.get("Dispensed").d("Dispensed")
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              {isOwnPrescription ? (
                <DmsAlertInfo message={intl.get("cancel_prescription_to_create_new").d("Cancel this prescription from the patient's prescription history to create a new one.")} />
              ) : (
                <DmsAlertWarning message={intl.get("cannot_create_prescription_other_doctor").d("Only the prescribing doctor can cancel this prescription.")} />
              )}
            </Box>
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          {isOwnPrescription ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DmsButton
                  variant="secondary"
                  text={intl.get("Close").d("Close")}
                  onClick={() => {
                    setShowActivePrescriptionWarning(false);
                    setActivePrescriptionForWarning(null);
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <DmsButton
                  variant="primary"
                  text={intl.get("go_to_my_prescriptions").d("Go to My Prescriptions")}
                  onClick={() => {
                    setShowActivePrescriptionWarning(false);
                    setActivePrescriptionForWarning(null);
                    navigate(APP_ROUTES.PrescriptionHistory);
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          ) : (
            <DmsButton
              variant="primary"
              text={intl.get("ok").d("OK")}
              onClick={() => {
                setShowActivePrescriptionWarning(false);
                setActivePrescriptionForWarning(null);
              }}
              fullWidth
            />
          )}
        </DmsModal.Footer>
      </DmsModal>

      {/* Recent Prescription Blocking Modal (19-day rule) */}
      <DmsModal
        isVisible={showRecentPrescriptionModal}
        onClose={() => {
          setShowRecentPrescriptionModal(false);
          setRecentPrescriptionInfo(null);
        }}
      >
        <DmsModal.Header
          title={intl.get("recent_prescription_blocking_title").d("Recent Prescription")}
          onClose={() => {
            setShowRecentPrescriptionModal(false);
            setRecentPrescriptionInfo(null);
          }}
        />
        <DmsModal.Body>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              {recentPrescriptionInfo?.blockReason === 'active' ? (
                // Check if it's the doctor's own prescription or another doctor's
                recentPrescriptionInfo?.isOwnPrescription ? (
                  intl.get("active_prescription_blocking_message_new").d(
                    "O seu paciente possui uma prescrição válida que ainda não foi dispensada. Para criar uma nova prescrição, cancele a prescrição atual primeiro."
                  )
                ) : (
                  intl.get("active_prescription_other_doctor_message").d(
                    "Este paciente possui uma prescrição válida criada por outro médico que ainda não foi dispensada. Não é possível criar uma nova prescrição."
                  )
                )
              ) : (
                intl.get("recent_prescription_blocking_message").d(
                  "You already have a prescription for this patient that is still valid. Therefore, until the expected expiration date (established according to RDC 735/22), it is not possible to issue a new prescription."
                )
              )}
            </Typography>

            {recentPrescriptionInfo?.prescription && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {intl.get("prescription_details").d("Prescription Details:")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("prescription_identifier").d("Identifier")}:</strong>{' '}
                      {recentPrescriptionInfo.prescription.prescription_identifier || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("prescription_date").d("Prescription Date")}:</strong>{' '}
                      {recentPrescriptionInfo.prescription.prescription_date
                        ? dayjs(recentPrescriptionInfo.prescription.prescription_date).format('DD/MM/YYYY')
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("ExpirationDate").d("Expiration Date")}:</strong>{' '}
                      {recentPrescriptionInfo.prescription.expiration_date
                        ? dayjs(recentPrescriptionInfo.prescription.expiration_date).format('DD/MM/YYYY')
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>{intl.get("status").d("Status")}:</strong>{' '}
                      {recentPrescriptionInfo.prescription.status === PRESCRIPTION_STATUS.ACTIVE
                        ? intl.get("Active").d("Active")
                        : recentPrescriptionInfo.prescription.status === PRESCRIPTION_STATUS.DISPENSED
                        ? intl.get("Dispensed").d("Dispensed")
                        : intl.get("Other").d("Other")}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {recentPrescriptionInfo?.blockReason === 'active' && (
              <Box sx={{ mt: 2 }}>
                {recentPrescriptionInfo?.isOwnPrescription ? (
                  <DmsAlertInfo
                    message={intl.get("cancel_prescription_instruction").d(
                      "Cancele esta prescrição pelo menu Minhas Prescrições e clique no botão de cancelar, antes de criar uma nova."
                    )}
                  />
                ) : (
                  <DmsAlertWarning
                    message={intl.get("cancel_prescription_instruction_other_doctor").d(
                      "Apenas o médico prescritor pode cancelar esta prescrição. Caso necessite excluir, por favor, envie um e-mail para programalena@eurofarma.com"
                    )}
                  />
                )}
              </Box>
            )}
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton
            variant="primary"
            text={intl.get("ok").d("OK")}
            onClick={() => {
              setShowRecentPrescriptionModal(false);
              setRecentPrescriptionInfo(null);
            }}
            fullWidth
          />
        </DmsModal.Footer>
      </DmsModal>
    </DmsPage>
  );
};

export default MyPatientsPage;
